import type { KpiTile, KpiTone } from '@/versions/v1/schema/plant-role';
import type { KpiCardProps, KpiDeltaDirection } from './KpiCard';
import type { VizTone } from './viz-tone';

const KPI_TONE_MAP: Record<KpiTone, VizTone> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
};

/** Trailing ` · [+−▲▼±]…` fragment at end of deck KPI sub lines (slides 2, 3, 6, 9, 11). */
const TRAILING_DELTA_RE = / · ([+−▲▼±].+)$/;

function deltaDirectionFromFragment(fragment: string): KpiDeltaDirection {
  if (fragment.startsWith('▼') || fragment.startsWith('−') || fragment.startsWith('-')) {
    return 'down';
  }
  if (fragment.startsWith('▲') || fragment.startsWith('+')) {
    return 'up';
  }
  return 'flat';
}

function deltaBasisSuffix(subLine: string): string {
  const lower = subLine.toLowerCase();
  if (/\bdesign\b/.test(lower) && !/\btarget\b/.test(lower)) {
    return 'vs design';
  }
  if (/\btarget\b/.test(lower)) {
    return 'vs Target';
  }
  return 'vs Target';
}

function formatDeltaText(fragment: string, subLine: string): string {
  const stripped = fragment.replace(/^[+−▲▼±]\s*/, '').trim();
  if (/vs/i.test(stripped)) {
    return stripped;
  }
  if (/[%]| pt/.test(stripped)) {
    const basis = deltaBasisSuffix(subLine);
    if (stripped.includes(' pt') && basis === 'vs Target') {
      return `${stripped} vs target`;
    }
    return `${stripped} ${basis}`;
  }
  return stripped;
}

function parseTrailingDelta(
  sub: string,
  tone: VizTone,
): { subLine: string; delta?: KpiCardProps['delta'] } {
  const match = sub.match(TRAILING_DELTA_RE);
  if (!match) {
    return { subLine: sub };
  }
  const fragment = match[1].trim();
  return {
    subLine: sub.slice(0, -match[0].length),
    delta: {
      text: formatDeltaText(fragment, sub.slice(0, -match[0].length)),
      direction: deltaDirectionFromFragment(fragment),
      tone,
    },
  };
}

/** Project a deck KPI tile into KpiCard props — splits trailing delta off the sub line. */
export function kpiCardPropsFromTile(tile: KpiTile): KpiCardProps {
  const tone = KPI_TONE_MAP[tile.tone];
  const { subLine, delta } = parseTrailingDelta(tile.sub, tone);
  return {
    label: tile.label,
    value: tile.value,
    sub: subLine || undefined,
    delta,
    tone,
  };
}
